import { authService } from '../auth/auth.service';
import store, { type AppDispatch } from '../store';
import Bus from '@whiz-cart/node-shared/connection/bus';
import { ConnectionStatus } from '@whiz-cart/node-shared/connection/connection';
import SignalrConnection from '@whiz-cart/node-shared/connection/signalrConnection';
import CPromise from '@whiz-cart/node-shared/cPromise';
import sleep from '@whiz-cart/node-shared/sleep';
import { bound } from '@whiz-cart/ui-shared/decorators/bound';
import _ from 'lodash';

export default class Connection<Dependencies extends string = never> extends Bus {
    refs = new Set();
    channel: unknown;
    storeConfig = {};
    staticConfig? = {};
    name = '';
    url: (deps: Record<Dependencies | string, string>) => string | undefined;

    constructor({
        url,
        dependencies,
        start,
        name,
    }: {
        url: string | ((deps: Record<Dependencies | string, string>) => string | undefined);
        name: string;
        dependencies?: Record<Dependencies, string>;
        start?: boolean;
    }) {
        super();
        this.url = typeof url === 'function' ? url : () => url;

        store.subscribeState(
            (state) =>
                _.mapValues(dependencies ?? {}, (value: ((state: any) => any) | string) =>
                    value instanceof Function ? value(state) : _.get(state, value),
                ),
            this._update,
        );

        if (start) this.start();
        this.name = name;
    }

    get config(): Record<Dependencies | string, string> {
        return { ...this.storeConfig, ...this.staticConfig } as Record<Dependencies | string, string>;
    }

    @bound start(config?: any) {
        const configChanged = !_.isEqual(this.staticConfig, config);
        this.staticConfig = config;

        const ref = {};
        this.refs.add(ref);
        if (this.refs.size === 1 || configChanged) this._connect();

        return async () => {
            delete this.staticConfig;
            await sleep(1000); // Don't disconnect immediately. Otherwise connections are reset on some page changes.
            this.refs.delete(ref);
            if (this.refs.size === 0) this._disconnect();
        };
    }

    @bound async _update(config: any) {
        this.storeConfig = config;

        this._disconnect();
        if (this.refs.size > 0) this._connect();
    }

    async _connect() {
        this._disconnect();

        let url;
        if ((url = await this.url(this.config))) {
            this.updateChannel(
                new SignalrConnection(url, {
                    accessTokenFactory: async () => {
                        const token = await authService.accessToken.get(undefined);
                        if (!token) {
                            throw Error('No access token available for connection!');
                        }
                        return token;
                    },
                    debug: console.debug,
                }),
            );
            console.debug(`Started connection: ${this.name}`);
        }
    }

    _disconnect() {
        if (this.channelIn) {
            this.updateChannel();
            console.debug(`Stopped connection: ${this.name}`);
        }
    }

    onConnection(
        action: () => CPromise<unknown> | unknown | ((dispatch: AppDispatch) => CPromise<unknown>),
        setLoading?: (isLoading: boolean) => void,
    ) {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let request: CPromise<unknown> | null | unknown | ((dispatch: AppDispatch) => CPromise<unknown>) = null;

        const trigger = async (preliminary?: boolean) => {
            try {
                request = action() ?? null;
                await request;
                setLoading?.(false);
                console.debug(`Executed after connection action: ${this.name}${preliminary ? ' (preliminary)' : ''}`);
            } catch (e) {
                if ((e as any).isCanceled) return;
                console.error(`Failed to execute after connection action: ${this.name}`, e);
                setLoading?.(false);
            }
        };

        return this.subscribeStatus((status) => {
            if (timer) {
                clearTimeout(timer);
            }

            if (typeof request === 'object' && request !== null && 'cancel' in request && typeof request.cancel === 'function') {
                request?.cancel?.();
            }

            if (status === ConnectionStatus.Connecting) {
                setLoading?.(true);
                timer = setTimeout(() => trigger(true), store.getState().config.preliminaryLoadingTimeout ?? 3000);
            } else if (status === ConnectionStatus.Connected) {
                setLoading?.(true);
                trigger();
            }
        });
    }
}
